/**
 * BranchingEditor — manages the survey's conditional show/hide rules (VER-109).
 *
 * Each rule reads "When <question> <operator> <value>, <show|hide> <target>".
 * Targets can be a question or a whole section. Rules are stored on
 * survey.branchingRules and resolved at runtime by branching.ts.
 */
import React from "react";
import {
    Alert,
    Box,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { AddOutlined, DeleteOutline } from "@mui/icons-material";
import { useFormikContext } from "formik";
import type { BranchingRule, BranchOperator, SurveyDefinition } from "./survey.types";
import { allQuestions } from "./survey.types";
import { nextLocalId } from "./builder.mock";
import { arrayRemove } from "./reorder";

const OPERATORS: { value: BranchOperator; label: string }[] = [
    { value: "eq", label: "is equal to" },
    { value: "neq", label: "is not equal to" },
    { value: "gt", label: "is greater than" },
    { value: "gte", label: "is at least" },
    { value: "lt", label: "is less than" },
    { value: "lte", label: "is at most" },
];

export const BranchingEditor = (): JSX.Element => {
    const { values, setFieldValue, handleChange, handleBlur } = useFormikContext<SurveyDefinition>();
    const rules = values.branchingRules;
    const questions = allQuestions(values);

    const setRules = (next: BranchingRule[]) => setFieldValue("branchingRules", next);

    const addRule = () => {
        const firstQuestion = questions[0];
        const rule: BranchingRule = {
            id: nextLocalId("rule"),
            sourceQuestionId: firstQuestion ? firstQuestion.id : "",
            operator: "eq",
            value: 0,
            action: "show",
            targetType: "question",
            targetId: "",
        };
        setRules([...rules, rule]);
    };

    const updateRule = (index: number, patch: Partial<BranchingRule>) =>
        setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));

    if (questions.length === 0) {
        return (
            <Alert severity="info">Add some questions first, then you can branch the survey on their answers.</Alert>
        );
    }

    return (
        <Box>
            <Typography variant="body2" color="textSecondary" mb={2}>
                Rules run in order. A &quot;show&quot; target is hidden until its rule matches; a &quot;hide&quot;
                target is visible until its rule matches. Rules on an unanswered question do not fire.
            </Typography>

            {rules.map((rule, index) => (
                <Paper
                    key={rule.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 2, borderRadius: (theme) => `${theme.shape.borderRadius}px` }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel id={`rule-${rule.id}-src`}>When</InputLabel>
                                <Select
                                    labelId={`rule-${rule.id}-src`}
                                    label="When"
                                    value={rule.sourceQuestionId}
                                    onChange={(e) => updateRule(index, { sourceQuestionId: e.target.value })}
                                >
                                    {questions.map((q, qi) => (
                                        <MenuItem key={q.id} value={q.id}>
                                            Q{qi + 1}: {q.text || "Untitled"}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={7} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel id={`rule-${rule.id}-op`}>Condition</InputLabel>
                                <Select
                                    labelId={`rule-${rule.id}-op`}
                                    label="Condition"
                                    value={rule.operator}
                                    onChange={(e) => updateRule(index, { operator: e.target.value as BranchOperator })}
                                >
                                    {OPERATORS.map((op) => (
                                        <MenuItem key={op.value} value={op.value}>
                                            {op.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={5} sm={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                name={`branchingRules.${index}.value`}
                                label="Value"
                                value={rule.value}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel id={`rule-${rule.id}-action`}>Action</InputLabel>
                                <Select
                                    labelId={`rule-${rule.id}-action`}
                                    label="Action"
                                    value={rule.action}
                                    onChange={(e) => updateRule(index, { action: e.target.value as "show" | "hide" })}
                                >
                                    <MenuItem value="show">Show</MenuItem>
                                    <MenuItem value="hide">Hide</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={1} display="flex" justifyContent="flex-end">
                            <Tooltip title="Remove rule">
                                <IconButton
                                    aria-label={`Remove rule ${index + 1}`}
                                    onClick={() => setRules(arrayRemove(rules, index))}
                                >
                                    <DeleteOutline fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel id={`rule-${rule.id}-ttype`}>Target type</InputLabel>
                                <Select
                                    labelId={`rule-${rule.id}-ttype`}
                                    label="Target type"
                                    value={rule.targetType}
                                    onChange={(e) =>
                                        updateRule(index, {
                                            targetType: e.target.value as "question" | "section",
                                            targetId: "",
                                        })
                                    }
                                >
                                    <MenuItem value="question">Question</MenuItem>
                                    <MenuItem value="section">Section</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <FormControl fullWidth size="small">
                                <InputLabel id={`rule-${rule.id}-target`}>Target</InputLabel>
                                <Select
                                    labelId={`rule-${rule.id}-target`}
                                    label="Target"
                                    value={rule.targetId}
                                    onChange={(e) => updateRule(index, { targetId: e.target.value })}
                                >
                                    {rule.targetType === "question"
                                        ? questions.map((q, qi) => (
                                              <MenuItem key={q.id} value={q.id}>
                                                  Q{qi + 1}: {q.text || "Untitled"}
                                              </MenuItem>
                                          ))
                                        : values.sections.map((s, si) => (
                                              <MenuItem key={s.id} value={s.id}>
                                                  Section {si + 1}: {s.title || "Untitled"}
                                              </MenuItem>
                                          ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>
            ))}

            <Button variant="outlined" startIcon={<AddOutlined />} onClick={addRule}>
                Add branching rule
            </Button>
        </Box>
    );
};
